// ProjectsTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { TabsContent } from '../../../components/ui/tabs';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { 
  FolderOpen, RefreshCw, Sparkles, Bot, Users, ListTodo, 
  Image as ImageIcon, ChevronRight, ChevronDown, Brain 
} from 'lucide-react';

export const ProjectsTab = ({
  projects = [],
  onRefresh,
  onProjectClick,
  onNavigateToDemo
}) => {
  return (
    <TabsContent value="projects" className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
            <FolderOpen className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-emerald-900">AI Projects</h2>
            <p className="text-emerald-700 mt-1">
              View projects created when AI agents delegate tasks. See the execution mind map, task outcomes, and Kanban workflow.
            </p>
          </div>
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FolderOpen className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">No Projects Yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Projects are created when AI agents assign tasks to other agents. Run a demo to create your first project.
            </p>
            <Button variant="outline" className="mt-4" onClick={onNavigateToDemo}>
              <Sparkles className="h-4 w-4 mr-2" />
              Go to Demo Mode
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <Card key={project.id} className="overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => onProjectClick(project)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-5 w-5 text-emerald-600" />
                      <h3 className="font-semibold text-lg">{project.title}</h3>
                      <Badge className={
                        project.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        project.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {project.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {project.description?.substring(0, 150)}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
                
                <div className="flex items-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Lead: {project.lead_agent || 'AI Cortex'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{project.agents_involved?.length || 0} agents</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ListTodo className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{project.task_count || 0} tasks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{project.deliverables_count || 0} deliverables</span>
                  </div>
                </div>

                {project.task_status_counts && (
                  <div className="mt-3 flex gap-1 h-2 rounded-full overflow-hidden bg-slate-100">
                    {project.task_status_counts.completed > 0 && (
                      <div 
                        className="bg-emerald-500" 
                        style={{ width: `${(project.task_status_counts.completed / project.task_count) * 100}%` }}
                        title={`${project.task_status_counts.completed} completed`}
                      />
                    )}
                    {project.task_status_counts.in_progress > 0 && (
                      <div 
                        className="bg-amber-500" 
                        style={{ width: `${(project.task_status_counts.in_progress / project.task_count) * 100}%` }}
                        title={`${project.task_status_counts.in_progress} in progress`}
                      />
                    )}
                    {project.task_status_counts.pending > 0 && (
                      <div 
                        className="bg-slate-300" 
                        style={{ width: `${(project.task_status_counts.pending / project.task_count) * 100}%` }}
                        title={`${project.task_status_counts.pending} pending`}
                      />
                    )}
                  </div>
                )}

                {project.cortex_summary && (
                  <Collapsible className="mt-4">
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700">
                      <Brain className="h-4 w-4" />
                      View AI Reasoning Summary
                      <ChevronDown className="h-4 w-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 p-3 bg-slate-50 rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-emerald-700">Cortex Synthesis:</span>
                          <p className="text-muted-foreground mt-1">{project.cortex_summary?.substring(0, 300)}...</p>
                        </div>
                        {project.left_brain_summary && (
                          <div>
                            <span className="font-medium text-blue-700">Left Brain (Analytical):</span>
                            <p className="text-muted-foreground mt-1">{project.left_brain_summary?.substring(0, 200)}...</p>
                          </div>
                        )}
                        {project.right_brain_summary && (
                          <div>
                            <span className="font-medium text-purple-700">Right Brain (Creative):</span>
                            <p className="text-muted-foreground mt-1">{project.right_brain_summary?.substring(0, 200)}...</p>
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </TabsContent>
  );
};

export default ProjectsTab;
